import { useFormContext } from "react-hook-form";
import { CotationFormData } from "@/types/forms/home/cotationFormData.form";
import { useGetCustomV1 } from "@/api/customs.api";
import { useMemo } from "react";
import {
  calculateCotationPrice,
  getExpeditionPriceParameters,
  numberOfShipmentsNeeded,
} from "@/../../shared/helpers/cotationPriceCalculation";
import {
  getTvaApplications,
  isInEuVatTerritory,
  isFrenchVatTerritory,
} from "@/../../shared/helpers/tvaApplicability";
import { useGetPreference } from "@/api/preference.api";
import { useGetCompany } from "@/api/company.api";
import { useGetGlobalParameters } from "@/api/globalParameters.api";
import { useGetWoodboxPriceCompany, useGetWoodboxPriceDefault } from "@/api/woodboxPrice.api";
import { GetWoodboxPriceResponse } from "@/types/api/api-types";
import { useParams } from "react-router-dom";
import { useGetOneExpedition } from "@/api/expedition.api";

const usePriceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data: preference } = useGetPreference();
  const { data: company } = useGetCompany();
  const { data: globalParameters } = useGetGlobalParameters();
  const { data: woodboxPriceDefault } = useGetWoodboxPriceDefault();
  const { data: woodboxPriceCompany } = useGetWoodboxPriceCompany();
  const { data: expedition } = useGetOneExpedition(id || "");

  const { watch } = useFormContext<CotationFormData>();

  const { data: custom } = useGetCustomV1(watch("country"));

  const formData = watch();

  const applicableWoodboxPrice: GetWoodboxPriceResponse = useMemo(() => {
    const relevantWoodboxPrice =
      woodboxPriceCompany && woodboxPriceCompany?.length > 0
        ? woodboxPriceCompany
        : woodboxPriceDefault || [];
    if (!custom || relevantWoodboxPrice.length === 0) return [];
    if (custom.label === "FRANCE") {
      return relevantWoodboxPrice.filter((e) => e.region === "FRANCE");
    } else if (custom.region === "EUROPEANUNION") {
      return relevantWoodboxPrice.filter((e) => e.region === "EUROPEANUNION");
    } else if (custom.region === "EUROPE") {
      return relevantWoodboxPrice.filter((e) => e.region === "EUROPE");
    } else {
      return relevantWoodboxPrice.filter((e) => e.region === "OTHER");
    }
  }, [woodboxPriceCompany, woodboxPriceDefault, custom]);

  const isDestinationEU = useMemo(
    () => custom != null && custom.flagCode != null && isInEuVatTerritory(custom.flagCode),
    [custom],
  );

  const billingEntityCountry = company?.billingEntities?.find((e) => e.isDefault)?.country ?? "FR";

  const isFrenchBillingEntity = isFrenchVatTerritory(billingEntityCountry);

  const priceDetail = useMemo(() => {
    if (!custom || !preference) return;

    const formDataFiltered = {
      ...formData,
      products: formData.products.filter(
        (product) => product.value !== undefined && product.bottleQuantity !== undefined,
      ),
    };

    const tvaApplications = getTvaApplications(billingEntityCountry, isDestinationEU);

    if (
      expedition?.quotation?.expeditionPriceParameters &&
      custom.id === expedition?.quotation?.customId
    ) {
      const { customTrimmed, insurancePercentage, woodBoxMembranesPriceHT, woodboxPrices } =
        getExpeditionPriceParameters(expedition?.quotation?.expeditionPriceParameters);
      return calculateCotationPrice(
        formData,
        customTrimmed,
        woodBoxMembranesPriceHT || 0,
        insurancePercentage || 0,
        woodboxPrices,
        tvaApplications,
      );
    }

    return calculateCotationPrice(
      formDataFiltered,
      custom,
      globalParameters?.woodBoxMembranesPriceHT || 0,
      company?.optionInsuranceActive
        ? company?.insurancePercentage || 0
        : globalParameters?.insurancePercentage || 0,
      applicableWoodboxPrice,
      tvaApplications,
    );
  }, [
    custom,
    formData,
    preference,
    company,
    globalParameters,
    applicableWoodboxPrice,
    expedition,
    isDestinationEU,
    billingEntityCountry,
  ]);

  const numberOfShipments = useMemo(() => {
    if (!custom) return;
    return numberOfShipmentsNeeded(custom.bottleMaxPerShipment, formData.products);
  }, [custom, formData]);

  return { priceDetail, custom, numberOfShipments, isDestinationEU, isFrenchBillingEntity };
};

export default usePriceDetail;
